import React, { useEffect } from "react";
import { Add, Clear, Close, Tune } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  Box,
  Typography,
  FormControl,
  TextField,
  Stack,
} from "@mui/material";
import { Formik, FieldArray, Form } from "formik";
import { getSelectDropdownUI } from "../../HelperFunctions/EmployeeFormHelpers";
import { Dropdown } from "../../HelperFunctions/PayrollDropdown";
import shortcutManager from "../../Utilities/ShortcutManager";

export const fieldOptions = [
  { label: "Corporation", type: "corporation" },
  { label: "SortBy", type: "sort" },
  { label: "Wage Cost Name", columnType: 4, type: "text" },
  { label: "Account Number", columnType: 2, type: "text" },
  { label: "Total Payment", columnType: 3, type: "number" },
  { label: "Status", columnType: 1, type: "status" },
];

export const textConditions = ["Starts With", "Ends With", "Contains", "Equal"];
export const numericConditions = ["None", ">=", "<=", "="];
export const statusOptions = [
  // "--All--",
  // "Completed",
  // "Pending",
  // "Resume",
  // "Deleted",
  "--All--", "Completed", "Attention Needed", "Failed"
];
export const sortByOptions = [
  // { label: "Entry Date", value: 101 },
  // { label: "Created Date", value: 102 },
  // { label: "Modified Date", value: 103 },
  { label: "Total Payment", value: 104 }, // Mapped to Amount
  { label: "Account Number", value: 105 },
  { label: "Corporation Name", value: 106 },
  { label: "PayPeriod", value: 107 },
];

const TEXT_OPERATOR_MAP = {
  Equal: 1,
  "Starts With": 2,
  "Ends With": 3,
  Contains: 4,
};

const NUMBER_OPERATOR_MAP = {
  "=": 1,
  ">=": 5,
  "<=": 6,
};

const STATUS_OPERATOR_MAP = {
  Completed: 8,
  Failed: 9,
  // Resume: 10,
  "Attention Needed": 10,
  // Deleted: 11,
};

const COLUMN_TYPE_TO_FIELD = {
  4: "Wage Cost Name",
  2: "Account Number",
  3: "Total Payment",
  1: "Status",
};

const OPERATOR_TO_TEXT_CONDITION = {
  1: "Equal",
  2: "Starts With",
  3: "Ends With",
  4: "Contains",
};

const OPERATOR_TO_NUMBER_CONDITION = {
  1: "=",
  5: ">=",
  6: "<=",
};

const OPERATOR_TO_STATUS = {
  8: "Completed",
  9: "Failed",
  10: "Attention Needed",
  // 11: "Deleted",
};

const SearchFilters = ({
  open,
  onClose,
  onApply,
  initialFilters,
  initialSortOrder,
  initialSortBy,
  corporationOptions = [],
  initialCorporation,
  hiddenFields = [],
}) => {
  const formikRef = React.useRef(null);
  const defaultFilter = {
    field: "Corporation",
    textCondition: "Starts With",
    numberCondition: "None",
    value: "",
    sortBy: Number(initialSortBy ?? 107),
    sortOrder: initialSortOrder ?? true,
    corporationValue: initialCorporation?.corpValue ?? "",
  };

  const mapToUiFilter = (filter) => {
    if (!filter) return { ...defaultFilter };
    if (filter.field) {
      return {
        field: filter.field,
        textCondition: filter.textCondition || "Starts With",
        numberCondition: filter.numberCondition || "None",
        value: filter.value ?? "",
        sortBy: Number(filter.sortBy ?? initialSortBy ?? 107),
        sortOrder: filter.sortOrder ?? initialSortOrder ?? true,
        corporationValue:
          filter.corporationValue ?? initialCorporation?.corpValue ?? "",
      };
    }

    const columnType = Number(filter.columnType ?? filter.ColumnType);
    const operator = Number(filter.operator ?? filter.Operator);
    const value = filter.value ?? filter.Value ?? "";
    const field = COLUMN_TYPE_TO_FIELD[columnType] || "";
    if (!field) return { ...defaultFilter };

    const fieldConfig = fieldOptions.find((opt) => opt.label === field);
    if (!fieldConfig) return { ...defaultFilter };

    if (fieldConfig.type === "text") {
      return {
        field,
        textCondition: OPERATOR_TO_TEXT_CONDITION[operator] || "Starts With",
        numberCondition: "None",
        value: value ?? "",
      };
    }

    if (fieldConfig.type === "number") {
      return {
        field,
        textCondition: "Starts With",
        numberCondition: OPERATOR_TO_NUMBER_CONDITION[operator] || "None",
        value: value ?? "",
      };
    }

    if (fieldConfig.type === "status") {
      return {
        field,
        textCondition: "Starts With",
        numberCondition: "None",
        value: OPERATOR_TO_STATUS[operator] || value || "--All--",
      };
    }

    return { ...defaultFilter };
  };

  const corporationFilterRow = {
    field: "Corporation",
    textCondition: "Starts With",
    numberCondition: "None",
    value: "",
    sortBy: Number(initialSortBy ?? 107),
    sortOrder: initialSortOrder ?? true,
    corporationValue: initialCorporation?.corpValue ?? "",
  };

  const sortFilterRow = {
    field: "SortBy",
    textCondition: "Starts With",
    numberCondition: "None",
    value: "",
    sortBy: Number(initialSortBy ?? 107),
    sortOrder: initialSortOrder ?? true,
    corporationValue: initialCorporation?.corpValue ?? "",
  };

  const mappedInitialFilters =
    initialFilters && initialFilters.length > 0
      ? initialFilters.map(mapToUiFilter)
      : [];

  const isSortByInOptions = sortByOptions.some(
    (opt) => opt.value === Number(initialSortBy),
  );
  const existingSortRow = mappedInitialFilters.find(
    (f) => f.field === "SortBy",
  );
  const existingCorporationRow = mappedInitialFilters.find(
    (f) => f.field === "Corporation",
  );
  const remainingRows = mappedInitialFilters.filter(
    (f) => f.field !== "SortBy" && f.field !== "Corporation",
  );
  const initialValues = {
    filters: [
      existingCorporationRow || corporationFilterRow,
      existingSortRow || (isSortByInOptions ? sortFilterRow : null),
      ...remainingRows,
    ]
      .filter(Boolean)
      .filter((f) => !hiddenFields.includes(f.field)),
  };
  const dropdownMenuProps = getSelectDropdownUI();

  const rowControlSx = {
    minWidth: 160,
    "& .MuiFilledInput-root": {
      height: 35,
      borderRadius: "7px",
      backgroundColor: "#eef0f2",
      "&:before, &:after": {
        display: "none",
      },
      "&:hover:not(.Mui-disabled):before": {
        display: "none",
      },
    },
    "& .MuiSelect-filled": {
      fontSize: 12,
      py: 0,
      minHeight: "35px !important",
      display: "flex",
      alignItems: "center",
      paddingLeft: "10px",
    },
    "& .Mui-disabled .MuiSelect-filled": {
      WebkitTextFillColor: "#323a45",
    },
  };

  const rowInputSx = {
    minWidth: 160,
    "& .MuiFilledInput-root": {
      height: 35,
      borderRadius: "7px",
      backgroundColor: "#eef0f2",
      "&:before, &:after": {
        display: "none",
      },
      "&:hover:not(.Mui-disabled):before": {
        display: "none",
      },
    },
    "& input": {
      fontSize: 12,
      padding: "0 10px",
    },
  };

  useEffect(() => {
    if (!open) return;
    shortcutManager.addShortcut("alt+a", () => {
      if (formikRef.current) {
        formikRef.current.submitForm();
      }
    });
    return () => shortcutManager.removeShortcut("alt+a");
  }, [open]);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          width: "84vw",
          maxWidth: "1220px",
          height: "90vh",
          maxHeight: "780px",
          // height: "auto",
          // maxHeight: "500px",
          borderRadius: "6px",
        },
      }}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values) => {
          let selectedSortOrder = initialSortOrder ?? true;
          let selectedSortBy = Number(initialSortBy ?? 107);
          let selectedCorporationValue = initialCorporation?.corpValue ?? "";
          const mappedFilters = values.filters
            .map((f) => {
              const fieldConfig = fieldOptions.find(
                (opt) => opt.label === f.field,
              );
              if (!fieldConfig) return null;

              if (fieldConfig.type === "sort") {
                selectedSortBy = Number(f.sortBy ?? selectedSortBy);
                selectedSortOrder =
                  f.sortOrder === true || f.sortOrder === "true";
                return null;
              }
              if (fieldConfig.type === "corporation") {
                selectedCorporationValue = f.corporationValue ?? "";
                return null;
              }

              let operator = 0;
              const value = f.value;

              if (fieldConfig.type === "text") {
                operator = TEXT_OPERATOR_MAP[f.textCondition] || 0;
              } else if (fieldConfig.type === "number") {
                operator = NUMBER_OPERATOR_MAP[f.numberCondition] || 0;
              } else if (fieldConfig.type === "status") {
                if (!value || value === "--All--") return null;
                operator = STATUS_OPERATOR_MAP[value] || 0;
              }

              if (!operator && fieldConfig.type !== 'number') return null;
              if (fieldConfig.type !== 'number' && (value === null || value === undefined || value === ""))
                return null;

              return {
                columnType: fieldConfig.columnType,
                operator,
                value,
              };
            })
            .filter(Boolean);

          onApply(
            mappedFilters,
            selectedSortOrder,
            selectedSortBy,
            selectedCorporationValue,
          );
        }}
      >
        {({ values, handleChange, setFieldValue }) => (
          <Form>
            <Box
              sx={{
                px: 1.5,
                py: 0.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #d5d9dd",
                backgroundColor: "#f4f6f8",
              }}
            >
              <Typography
                sx={{
                  color: "#2176ff",
                  fontWeight: 500,
                  fontSize: 16,
                }}
              >
                Filters
              </Typography>
              <IconButton
                size="small"
                edge="end"
                onClick={onClose}
                sx={{
                  backgroundColor: "#686868",
                  borderRadius: 1,
                }}
                disableRipple
              >
                <Close sx={{ color: "white", fontSize: "11px" }} />
              </IconButton>
            </Box>

            <DialogContent sx={{ p: 0, overflowY: "auto" }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Tune sx={{ color: "#5f6368", fontSize: 18 }} />
                  <Typography
                    sx={{ color: "#525a63", fontSize: 12, fontWeight: 500 }}
                  >
                    Customize the filter
                  </Typography>
                </Stack>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  sx={{
                    textTransform: "none",
                    borderRadius: "6px",
                    minWidth: 72,
                    height: 32,
                    fontSize: 12,
                    color: "#081018",
                    backgroundColor: "#bfe7ff",
                    "&:hover": {
                      backgroundColor: "#a9ddff",
                    },
                  }}
                >
                  <u>A</u>pply
                </Button>
              </Box>

              <FieldArray name="filters">
                {({ push, remove }) => (
                  <Box sx={{ px: 2, pb: 2 }}>
                    {values.filters.map((item, index) => {
                      const selectedField = item.field;
                      const isTextField =
                        selectedField === "Wage Cost Name" ||
                        selectedField === "Account Number";
                      const isNumberField = selectedField === "Total Payment";
                      const isStatusField = selectedField === "Status";
                      const isSortField = selectedField === "SortBy";
                      const isCorporationField =
                        selectedField === "Corporation";
                      const selectedInOtherRows = values.filters
                        .filter((_, i) => i !== index)
                        .map((f) => f.field)
                        .filter(Boolean);
                      const availableFieldOptions = fieldOptions.filter(
                        (field) => {
                          const isUniqueField =
                            field.label === "Corporation" ||
                            field.label === "SortBy";
                          return (
                            (field.label === item.field ||
                              !isUniqueField ||
                              !selectedInOtherRows.includes(field.label)) &&
                            !hiddenFields.includes(field.label)
                          );
                        },
                      );
                      const isFixedField = isCorporationField;

                      return (
                        <Stack
                          key={index}
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                          sx={{
                            mb: 1.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <FormControl size="small" sx={rowControlSx}>
                            <Select
                              name={`filters[${index}].field`}
                              value={item.field}
                              onChange={handleChange}
                              disabled={isFixedField}
                              variant="filled"
                              MenuProps={dropdownMenuProps}
                              displayEmpty
                            >
                              {availableFieldOptions.map((field, i) => (
                                <MenuItem key={i} value={field.label}>
                                  {field.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {isTextField && (
                            <>
                              <FormControl size="small" sx={rowControlSx}>
                                <Select
                                  name={`filters[${index}].textCondition`}
                                  value={item.textCondition}
                                  onChange={handleChange}
                                  variant="filled"
                                  MenuProps={dropdownMenuProps}
                                  displayEmpty
                                >
                                  {textConditions.map((cond, i) => (
                                    <MenuItem key={i} value={cond}>
                                      {cond}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              <TextField
                                size="small"
                                variant="filled"
                                placeholder="Enter value..."
                                name={`filters[${index}].value`}
                                value={item.value}
                                onChange={handleChange}
                                sx={rowInputSx}
                              />
                            </>
                          )}

                          {isNumberField && (
                            <>
                              <FormControl size="small" sx={rowControlSx}>
                                <Select
                                  name={`filters[${index}].numberCondition`}
                                  value={item.numberCondition}
                                  onChange={handleChange}
                                  variant="filled"
                                  MenuProps={dropdownMenuProps}
                                  displayEmpty
                                >
                                  {numericConditions.map((cond, i) => (
                                    <MenuItem key={i} value={cond}>
                                      {cond}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              {item.numberCondition !== "None" && (
                                <TextField
                                  size="small"
                                  variant="filled"
                                  type="number"
                                  placeholder="Enter amount..."
                                  name={`filters[${index}].value`}
                                  value={item.value}
                                  onChange={handleChange}
                                  sx={rowInputSx}
                                />
                              )}
                            </>
                          )}

                          {isStatusField && (
                            <FormControl size="small" sx={rowControlSx}>
                              <Select
                                name={`filters[${index}].value`}
                                value={item.value || "--All--"}
                                onChange={handleChange}
                                variant="filled"
                                MenuProps={dropdownMenuProps}
                                displayEmpty
                              >
                                {statusOptions.map((status, i) => (
                                  <MenuItem key={i} value={status}>
                                    {status}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          {isSortField && (
                            <>
                              <FormControl size="small" sx={rowControlSx}>
                                <Select
                                  name={`filters[${index}].sortBy`}
                                  value={Number(
                                    item.sortBy ?? initialSortBy ?? 107,
                                  )}
                                  onChange={handleChange}
                                  variant="filled"
                                  MenuProps={dropdownMenuProps}
                                  displayEmpty
                                >
                                  {sortByOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              <FormControl size="small" sx={rowControlSx}>
                                <Select
                                  name={`filters[${index}].sortOrder`}
                                  value={String(
                                    item.sortOrder ?? initialSortOrder ?? true,
                                  )}
                                  onChange={(e) =>
                                    setFieldValue(
                                      `filters[${index}].sortOrder`,
                                      e.target.value === "true",
                                    )
                                  }
                                  variant="filled"
                                  MenuProps={dropdownMenuProps}
                                  displayEmpty
                                >
                                  <MenuItem value="false">Ascending</MenuItem>
                                  <MenuItem value="true">Descending</MenuItem>
                                </Select>
                              </FormControl>
                            </>
                          )}

                          {isCorporationField && (
                            <Box sx={{ minWidth: 160 }}>
                              <Dropdown
                                name={`filters[${index}].corporationValue`}
                                options={corporationOptions}
                                getOptionLabel={(option) => option?.corpName || ""}
                                isEqual={(option, value) =>
                                  String(option?.corpValue) === String(value?.corpValue)
                                }
                                value={
                                  corporationOptions.find(
                                    (c) =>
                                      String(c.corpValue) ===
                                      String(
                                        item.corporationValue ??
                                        initialCorporation?.corpValue ??
                                        ""
                                      )
                                  ) || null
                                }
                                onChange={(name, newValue) => {
                                  setFieldValue(
                                    `filters[${index}].corporationValue`,
                                    newValue ? newValue.corpValue : ""
                                  );
                                }}
                                sx={{
                                  ...rowControlSx,
                                  "& .MuiFilledInput-root": {
                                    ...rowControlSx["& .MuiFilledInput-root"],
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                    display: "flex",
                                    alignItems: "center",
                                  },
                                  "& .MuiFilledInput-input": {
                                    fontSize: 12,
                                    padding: "0 10px !important",
                                  },
                                }}
                              />
                            </Box>
                          )}

                          <Button
                            type="button"
                            disableElevation
                            disabled={isFixedField}
                            startIcon={<Clear sx={{ fontSize: 14 }} />}
                            onClick={() => remove(index)}
                            sx={{
                              textTransform: "none",
                              minWidth: 100,
                              height: 30,
                              borderRadius: "8px",
                              fontSize: 12,
                              fontWeight: 500,
                              color: isFixedField ? "#e38a9f" : "#d9001B9A",
                              border: isFixedField
                                ? "1px solid #e8a4b4"
                                : "none",
                              backgroundColor: isFixedField
                                ? "#fff7fa"
                                : "#efdbe3",
                              "& .MuiButton-startIcon": {
                                marginRight: "2px",
                                marginLeft: "0px",
                              },
                              "&:hover": {
                                backgroundColor: isFixedField
                                  ? "#fff7fa"
                                  : "#e2bcc9",
                              },
                              "&.Mui-disabled": {
                                color: "#e38a9f",
                                border: "1px solid #e8a4b4",
                                backgroundColor: "#fff7fa",
                              },
                            }}
                          >
                            Remove
                          </Button>
                        </Stack>
                      );
                    })}

                    <Button
                      type="button"
                      onClick={() => {
                        const selectedFields = values.filters
                          .map((f) => f.field)
                          .filter(Boolean);
                        const nextAvailableField = fieldOptions.find(
                          (field) => {
                            const isUniqueField =
                              field.label === "Corporation" ||
                              field.label === "SortBy";
                            return (
                              (!isUniqueField ||
                                !selectedFields.includes(field.label)) &&
                              !hiddenFields.includes(field.label)
                            );
                          },
                        );
                        if (!nextAvailableField) return;

                        const defaultSortVal = sortByOptions.some(
                          (o) => o.value === Number(initialSortBy),
                        )
                          ? Number(initialSortBy)
                          : 107;
                        push({
                          field: nextAvailableField.label,
                          textCondition: "Starts With",
                          numberCondition: "None",
                          value: "",
                          sortBy: defaultSortVal,
                          sortOrder: initialSortOrder ?? true,
                          corporationValue: initialCorporation?.corpValue ?? "",
                        });
                      }}
                      size="small"
                      variant="outlined"
                      disableRipple
                      disableElevation
                      disabled={
                        !fieldOptions.some((field) => {
                          const isUniqueField =
                            field.label === "Corporation" ||
                            field.label === "SortBy";
                          return (
                            (!isUniqueField ||
                              !values.filters.some(
                                (f) => f.field === field.label,
                              )) &&
                            !hiddenFields.includes(field.label)
                          );
                        })
                      }
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        borderColor: "#e3e6e8",
                        backgroundColor: "#f3f4f5",
                        color: "#444d57",
                        fontSize: 12,
                        fontWeight: 500,
                        px: 1.8,
                        "&:hover": {
                          borderColor: "#d6dadd",
                          backgroundColor: "#eceeef",
                        },
                      }}
                      startIcon={<Add />}
                    >
                      Add Filter
                    </Button>
                  </Box>
                )}
              </FieldArray>
            </DialogContent>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
export default SearchFilters;
